import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type {
  AuthorityBasis,
  AuthorityBasisKind,
  AuthorityProjection,
  AuthorityProjectionRequest,
  AuthorityProjectionResourceType,
  CanonicalAction,
  CanonicalCapability,
  JsonObject,
  JsonValue,
  RepresentedEntityType,
} from '@/types/authorityProjection';

export const AUTHORITY_PROJECTION_RPC = 'get_current_authority_projection_secure' as const;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const AUTHORITY_IDENTIFIER_PATTERN = /^[a-z][a-z0-9]*(?:[._][a-z0-9]+)+$/;

const REPRESENTED_ENTITY_TYPES = new Set<RepresentedEntityType>(['company']);
const RESOURCE_TYPES = new Set<AuthorityProjectionResourceType>([
  'organization', 'workspace', 'company', 'project', 'investment', 'report',
  'decision', 'task', 'gate', 'milestone', 'evidence_item',
  'evidence_category', 'document', 'vault',
]);
const BASIS_KINDS = new Set<AuthorityBasisKind>([
  'organization_membership', 'workspace_membership', 'scoped_relationship',
  'project_authority', 'validator_assignment', 'gate_validator_assignment',
  'gate_material_grant', 'decision_duty', 'signatory_mandate', 'capital_mandate',
  'platform_privilege',
]);
const ACCOUNT_STATUSES = new Set(['active', 'pending', 'suspended', 'disabled'] as const);
const REQUIRED_REVISIONS = [
  'organizationMembership',
  'workspaceMembership',
  'scopedRelationships',
  'projectAuthorities',
  'validatorAssignments',
  'policy',
] as const;

function decodePathSegment(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const decoded = decodeURIComponent(value);
    return decoded.trim().length > 0 ? decoded : null;
  } catch {
    return null;
  }
}

/**
 * Resolve only resources whose workspace relationship can be validated by the
 * projection RPC today.  Parent-derived resources (tasks/documents/milestones)
 * stay on the workspace projection until their parent lookup is part of the
 * server contract; guessing that relationship here would create false denies.
 */
export function authorityResourceForWorkspacePath(
  pathname: string,
  workspaceId: string,
): AuthorityProjectionRequest['resource'] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 4 || segments[0] !== 'workspaces') return null;
  if (decodePathSegment(segments[1]) !== workspaceId) return null;

  const resourceId = decodePathSegment(segments[3]);
  if (segments[2] === 'projects' && resourceId) {
    return { type: 'project', id: resourceId };
  }
  return null;
}

export function isCanonicalAuthorityUuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

export class AuthorityProjectionValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthorityProjectionValidationError';
  }
}

export class AuthorityProjectionRpcError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthorityProjectionRpcError';
  }
}

type RecordValue = Record<string, unknown>;

function fail(path: string, expectation: string): never {
  throw new AuthorityProjectionValidationError(`${path} must be ${expectation}`);
}

function record(value: unknown, path: string): RecordValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    fail(path, 'an object');
  }
  return value as RecordValue;
}

function string(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    fail(path, 'a non-empty string');
  }
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  if (value === null) return null;
  return string(value, path);
}

function uuid(value: unknown, path: string): string {
  const parsed = string(value, path);
  if (!isCanonicalAuthorityUuid(parsed)) fail(path, 'a UUID');
  return parsed;
}

function nullableUuid(value: unknown, path: string): string | null {
  if (value === null) return null;
  return uuid(value, path);
}

function timestamp(value: unknown, path: string): string {
  const parsed = string(value, path);
  if (!Number.isFinite(Date.parse(parsed))) fail(path, 'an ISO timestamp');
  return parsed;
}

function nullableTimestamp(value: unknown, path: string): string | null {
  if (value === null) return null;
  return timestamp(value, path);
}

function nonNegativeInteger(value: unknown, path: string): number {
  if (!Number.isInteger(value) || (value as number) < 0) {
    fail(path, 'a non-negative integer');
  }
  return value as number;
}

function jsonValue(value: unknown, path: string): JsonValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value)) return value.map((item, index) => jsonValue(item, `${path}[${index}]`));
  const parsed = record(value, path);
  const result = Object.create(null) as Record<string, JsonValue>;
  for (const [key, item] of Object.entries(parsed)) {
    result[key] = jsonValue(item, `${path}.${key}`);
  }
  return result;
}

function jsonObject(value: unknown, path: string): JsonObject {
  const parsed = jsonValue(value, path);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) fail(path, 'a JSON object');
  return parsed as JsonObject;
}

function nullableRepresentedEntity(value: unknown, path: string) {
  if (value === null) return null;
  const parsed = record(value, path);
  const type = string(parsed.type, `${path}.type`) as RepresentedEntityType;
  if (!REPRESENTED_ENTITY_TYPES.has(type)) fail(`${path}.type`, 'a supported represented-entity type');
  return { type, id: uuid(parsed.id, `${path}.id`) } as const;
}

function nullableResource(value: unknown, path: string) {
  if (value === null) return null;
  const parsed = record(value, path);
  const type = string(parsed.type, `${path}.type`) as AuthorityProjectionResourceType;
  if (!RESOURCE_TYPES.has(type)) fail(`${path}.type`, 'a supported resource type');
  return { type, id: string(parsed.id, `${path}.id`) } as const;
}

function authorityIdentifier(value: unknown, path: string): string {
  const parsed = string(value, path);
  if (!AUTHORITY_IDENTIFIER_PATTERN.test(parsed)) {
    fail(path, 'a canonical dotted or underscored identifier');
  }
  return parsed;
}

function uniqueAuthorityIdentifiers<T extends CanonicalCapability | CanonicalAction>(
  value: unknown,
  path: string,
): readonly T[] {
  if (!Array.isArray(value)) fail(path, 'an array');
  const identifiers = value.map((item, index) => authorityIdentifier(item, `${path}[${index}]`) as T);
  if (new Set(identifiers).size !== identifiers.length) fail(path, 'an array without duplicates');
  return identifiers;
}

function authorityBasis(value: unknown): readonly AuthorityBasis[] {
  if (!Array.isArray(value)) fail('authorityBasis', 'an array');
  return value.map((item, index) => {
    const path = `authorityBasis[${index}]`;
    const parsed = record(item, path);
    const kind = string(parsed.kind, `${path}.kind`) as AuthorityBasisKind;
    if (!BASIS_KINDS.has(kind)) fail(`${path}.kind`, 'a supported authority-basis kind');
    return {
      kind,
      id: string(parsed.id, `${path}.id`),
      roleOrType: string(parsed.roleOrType, `${path}.roleOrType`),
      scope: jsonObject(parsed.scope, `${path}.scope`),
      revision: nonNegativeInteger(parsed.revision, `${path}.revision`),
      effectiveUntil: nullableTimestamp(parsed.effectiveUntil, `${path}.effectiveUntil`),
    };
  });
}

function revisions(value: unknown): AuthorityProjection['revisions'] {
  const parsed = record(value, 'revisions');
  for (const required of REQUIRED_REVISIONS) {
    if (!(required in parsed)) fail(`revisions.${required}`, 'present');
  }
  const result: Record<string, number> = {};
  for (const [key, revision] of Object.entries(parsed)) {
    result[key] = nonNegativeInteger(revision, `revisions.${key}`);
  }
  return result as AuthorityProjection['revisions'];
}

/** Parse and validate every authority-bearing field before it reaches UI state. */
export function parseAuthorityProjection(value: unknown): AuthorityProjection {
  const root = record(value, 'projection');
  if (root.schemaVersion !== 1) fail('schemaVersion', 'the supported version 1');

  const actor = record(root.actor, 'actor');
  const accountStatus = string(actor.accountStatus, 'actor.accountStatus') as AuthorityProjection['actor']['accountStatus'];
  if (!ACCOUNT_STATUSES.has(accountStatus)) fail('actor.accountStatus', 'a supported account status');

  const scope = record(root.scope, 'scope');
  const decision = record(root.decision, 'decision');
  if (typeof decision.allowed !== 'boolean') fail('decision.allowed', 'a boolean');
  const reasonCode = nullableString(decision.reasonCode, 'decision.reasonCode');
  if (!decision.allowed && reasonCode === null) fail('decision.reasonCode', 'present for a denied decision');
  const organizationId = nullableUuid(scope.organizationId, 'scope.organizationId');
  if (decision.allowed && organizationId === null) {
    fail('scope.organizationId', 'a UUID for an allowed decision');
  }

  return {
    schemaVersion: 1,
    projectionId: string(root.projectionId, 'projectionId'),
    computedAt: timestamp(root.computedAt, 'computedAt'),
    actor: {
      userId: uuid(actor.userId, 'actor.userId'),
      accountStatus,
    },
    scope: {
      organizationId,
      workspaceId: nullableUuid(scope.workspaceId, 'scope.workspaceId'),
      representedEntity: nullableRepresentedEntity(scope.representedEntity, 'scope.representedEntity'),
      resource: nullableResource(scope.resource, 'scope.resource'),
      actionContext: jsonObject(scope.actionContext, 'scope.actionContext'),
    },
    decision: {
      requestedAction: nullableString(decision.requestedAction, 'decision.requestedAction'),
      allowed: decision.allowed,
      reasonCode,
    },
    capabilities: uniqueAuthorityIdentifiers<CanonicalCapability>(root.capabilities, 'capabilities'),
    availableActions: uniqueAuthorityIdentifiers<CanonicalAction>(root.availableActions, 'availableActions'),
    authorityBasis: authorityBasis(root.authorityBasis),
    revisions: revisions(root.revisions),
  };
}

function canonicalJson(value: JsonValue): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`).join(',')}}`;
}

/** Stable scope identity used to prevent an old route projection from rendering. */
export function authorityProjectionRequestKey(request: AuthorityProjectionRequest): string {
  return canonicalJson({
    actorId: request.actorId,
    organizationId: request.organizationId,
    workspaceId: request.workspaceId,
    representedEntity: request.representedEntity,
    resource: request.resource,
    requestedAction: request.requestedAction,
    actionContext: request.actionContext,
  });
}

/**
 * Whether a server projection owns the canonical route currently rendered by
 * the application shell. Navigation must never reuse a projection from a
 * previous workspace/company, or infer one from browser profile fields.
 */
export function authorityProjectionMatchesNavigationPath(
  request: AuthorityProjectionRequest,
  projection: AuthorityProjection,
  pathname: string,
): boolean {
  const workspaceMatch = /^\/workspaces\/([^/]+)(?:\/|$)/.exec(pathname);
  if (workspaceMatch && workspaceMatch[1] !== 'select') {
    const workspaceId = decodePathSegment(workspaceMatch[1]);
    if (!workspaceId
      || request.workspaceId !== workspaceId
      || projection.scope.workspaceId !== workspaceId) {
      return false;
    }
    const expectedResource = authorityResourceForWorkspacePath(pathname, workspaceId);
    return sameOptionalReference(request.resource, expectedResource)
      && sameOptionalReference(projection.scope.resource, expectedResource);
  }

  const companyMatch = /^\/companies\/([^/]+)(?:\/|$)/.exec(pathname);
  if (companyMatch && companyMatch[1] !== 'select') {
    const companyId = decodePathSegment(companyMatch[1]);
    if (!companyId) return false;
    const expectedCompany = { type: 'company', id: companyId } as const;
    return sameOptionalReference(request.representedEntity, expectedCompany)
      && sameOptionalReference(request.resource, expectedCompany)
      && sameOptionalReference(projection.scope.representedEntity, expectedCompany)
      && sameOptionalReference(projection.scope.resource, expectedCompany);
  }

  return false;
}

function sameOptionalReference(
  actual: { readonly type: string; readonly id: string } | null,
  expected: { readonly type: string; readonly id: string } | null,
): boolean {
  return actual === null
    ? expected === null
    : expected !== null && actual.type === expected.type && actual.id === expected.id;
}

/** Prevent a valid projection for a different actor/scope from entering this session. */
export function assertProjectionMatchesRequest(
  projection: AuthorityProjection,
  request: AuthorityProjectionRequest,
): void {
  if (projection.actor.userId !== request.actorId) fail('actor.userId', 'the requested actor');
  // A null request organization means the route supplied only a synthetic
  // hint. The RPC must derive and return the canonical organization from the
  // validated workspace relationship; a non-null request must match exactly.
  if (request.organizationId !== null && projection.scope.organizationId !== request.organizationId) {
    fail('scope.organizationId', 'the requested organization');
  }
  if (request.workspaceId !== null) {
    if (projection.scope.workspaceId !== request.workspaceId) fail('scope.workspaceId', 'the requested workspace');
  } else if (projection.scope.workspaceId !== null) {
    // A canonical company route has no workspace segment. The server may
    // derive its workspace only while echoing the exact represented company
    // and exact company resource supplied by that route.
    const derivesWorkspaceFromCompany = request.representedEntity?.type === 'company'
      && request.resource?.type === 'company'
      && request.resource.id === request.representedEntity.id;
    if (!derivesWorkspaceFromCompany) fail('scope.workspaceId', 'the requested workspace');
  }
  // Canonical project URLs intentionally carry no company identifier. The
  // secure RPC may derive one unique represented company from the actor's
  // current, exact project chain. Accept that server-owned refinement only for
  // the same exact project resource; every caller-supplied representation must
  // still match byte-for-byte.
  const serverDerivedProjectRepresentation = request.representedEntity === null
    && projection.scope.representedEntity?.type === 'company'
    && request.resource?.type === 'project'
    && projection.scope.resource?.type === 'project'
    && projection.scope.resource.id === request.resource.id;
  if (!serverDerivedProjectRepresentation
    && !sameOptionalReference(projection.scope.representedEntity, request.representedEntity)) {
    fail('scope.representedEntity', 'the requested represented entity');
  }
  if (!sameOptionalReference(projection.scope.resource, request.resource)) {
    fail('scope.resource', 'the requested resource');
  }
  if (projection.decision.requestedAction !== request.requestedAction) {
    fail('decision.requestedAction', 'the requested action');
  }
  if (canonicalJson(projection.scope.actionContext) !== canonicalJson(request.actionContext)) {
    fail('scope.actionContext', 'the requested action context');
  }
}

interface ProjectionRpcClient {
  rpc(
    functionName: string,
    params: Record<string, unknown>,
  ): PromiseLike<{ data: unknown; error: { message?: string } | null }>;
}

interface ProjectWorkspaceLookupClient {
  from(table: string): {
    select(columns: string): {
      eq(column: string, value: string): {
        maybeSingle(): PromiseLike<{
          data: { workspace_id?: unknown } | null;
          error: { message?: string } | null;
        }>;
      };
    };
  };
}

/**
 * Resolve the canonical workspace carrier for a legacy project deep link.
 *
 * This is only a routing hint: the projects SELECT remains RLS-filtered and
 * the canonical destination independently requests an exact authority
 * projection before rendering. No user id, role, or browser relationship is
 * supplied to the lookup.
 */
export async function resolveLegacyProjectWorkspaceId(
  projectId: string,
  client: ProjectWorkspaceLookupClient = supabase as unknown as ProjectWorkspaceLookupClient,
): Promise<string | null> {
  if (!projectId.trim()) return null;
  const { data, error } = await client
    .from('projects')
    .select('workspace_id')
    .eq('id', projectId)
    .maybeSingle();
  if (error) {
    throw new AuthorityProjectionRpcError(error.message || 'Project route resolution failed');
  }
  const workspaceId = typeof data?.workspace_id === 'string' ? data.workspace_id : null;
  return workspaceId && isCanonicalAuthorityUuid(workspaceId) ? workspaceId : null;
}

export function authorityProjectionRpcParams(request: AuthorityProjectionRequest): Record<string, unknown> {
  return {
    p_organization_id: request.organizationId,
    p_workspace_id: request.workspaceId,
    p_represented_entity_type: request.representedEntity?.type ?? null,
    p_represented_entity_id: request.representedEntity?.id ?? null,
    p_resource_type: request.resource?.type ?? null,
    p_resource_id: request.resource?.id ?? null,
    p_requested_action: request.requestedAction,
    p_action_context: request.actionContext,
  };
}

/** Calls the actor-derived RPC; no actor id is ever supplied to Postgres. */
export async function fetchCurrentAuthorityProjection(
  request: AuthorityProjectionRequest,
  client: ProjectionRpcClient = supabase as unknown as SupabaseClient,
): Promise<AuthorityProjection> {
  let result: { data: unknown; error: { message?: string } | null };
  try {
    result = await client.rpc(AUTHORITY_PROJECTION_RPC, authorityProjectionRpcParams(request));
  } catch (error) {
    throw new AuthorityProjectionRpcError(error instanceof Error ? error.message : 'Authority projection RPC is unavailable');
  }
  if (result.error) {
    throw new AuthorityProjectionRpcError(result.error.message || 'Authority projection RPC failed');
  }
  const projection = parseAuthorityProjection(result.data);
  assertProjectionMatchesRequest(projection, request);
  return projection;
}
